import React, { FC, useState, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { RouteComponentProps } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { Button, Checkbox, TextField, FormControl, FormHelperText, IconButton, Select, MenuItem } from '@mui/material';
import { AddCircle, RemoveCircle, Refresh } from '@mui/icons-material';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';
import { useCommonStyles } from 'src/app/common/styles/common-styles';
import { preFillZeroList, ErrorFieldType, ErrorFieldDef, useErrorHandler } from 'src/app/common/utils';
import { MANDATORY_FIELD_ERROR_TEXT } from 'src/app/common/constants';
import { reassignLeadPath } from '../ReassignLeadRoutes';
import {
  ReassignAgentItem,
  ReassignSourceTypeEnum,
  LeadActivityStatusEnum,
  UpdateActivityStatusEnum,
  LeadScopingTypeEnum,
} from 'src/app/modules/PulseLeads/types/agent-types';
import { CampaignDropdownList } from 'src/app/modules/PulseLeads/types/campaign-types';
import {
  fetchScopeDropdown,
  reassignLead,
  GetScopeDropdownParam,
  ReassignLeadBody,
} from 'src/app/modules/PulseLeads/network/agentCrud';

const FIELD_CONTAINER_WIDTH = 300;

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: 20,
    marginBottom: 20,
    borderRadius: 5,
    backgroundColor: theme.palette.common.white,
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 15,
  },
  sectionMargin: {
    marginBottom: 15,
  },
  fieldContainer: {
    width: FIELD_CONTAINER_WIDTH,
    boxSizing: 'border-box',
  },
  mandatory: {
    color: 'red',
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  checkboxPadding: {
    marginLeft: -9,
  },
  iconButton: {
    padding: 9,
  },
  icon: {
    width: '1.2em',
    height: '1.2em',
  },
}));

type ReassignLeadFormState = {
  reassignAgentList: ReassignAgentItem[];
  reassignSourceType?: string;
  specificCampaignList?: string[];
  specificLeadSourceList?: string[];
  leadActivityStatus?: string;
  isCopyEngagement: boolean;
  isCopyCalendar: boolean;
  updateActivityStatus?: string;
  isAddReassignLabel: boolean;
  isSendNotification: boolean;
};

const initialReassignAgentItem: ReassignAgentItem = {
  fromAgent: '',
  toAgent: '',
};

const initialAgentListErrorState = {
  fromAgent: false,
  toAgent: false,
};

const initialState: ReassignLeadFormState = {
  reassignAgentList: [{ ...initialReassignAgentItem }],
  reassignSourceType: undefined,
  specificCampaignList: undefined,
  specificLeadSourceList: undefined,
  leadActivityStatus: undefined,
  isCopyEngagement: true,
  isCopyCalendar: true,
  updateActivityStatus: undefined,
  isAddReassignLabel: true,
  isSendNotification: true,
};

type ModifyFieldAction = {
  type: 'MODIFY_FIELD';
  payload: {
    field: keyof ReassignLeadFormState;
    value: any;
  };
};

type AgentLeadSourceFormAction = ModifyFieldAction;

const formReducer = (state: ReassignLeadFormState, action: AgentLeadSourceFormAction): ReassignLeadFormState => {
  switch (action.type) {
    case 'MODIFY_FIELD':
      return {
        ...state,
        [action.payload.field]: action.payload.value,
      };
    default:
      return state;
  }
};

const ReassignLeadDetailPage: FC<RouteComponentProps> = ({ history }) => {
  const dispatch = useDispatch();
  const { classes: commonClasses } = useCommonStyles();
  const { classes } = useStyles();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const [formState, formDispatch] = useReducer(formReducer, initialState);
  const [agentListErrorState, setAgentListErrorState] = useState<any>([{ ...initialAgentListErrorState }]);
  const [dropdownList, setDropdownList] = useState<CampaignDropdownList>();

  const reloadDropdown = async (reassignSourceType?: string) => {
    const agentListValidator = onValidateAgentList();
    if (
      !agentListValidator.hasError &&
      reassignSourceType &&
      (reassignSourceType === ReassignSourceTypeEnum.SPECIFIC_LEAD_CAMPAIGN ||
        reassignSourceType === ReassignSourceTypeEnum.SPECIFIC_LEAD_SOURCE)
    ) {
      const scopeType =
        reassignSourceType === ReassignSourceTypeEnum.SPECIFIC_LEAD_CAMPAIGN
          ? LeadScopingTypeEnum.CAMPAIGN
          : LeadScopingTypeEnum.LEAD_SOURCE;
      const params: GetScopeDropdownParam = {
        agents: preFillZeroList(formState.reassignAgentList.map((item) => item.fromAgent)),
        scopeType,
      };
      const dropdownRes = await fetchScopeDropdown(params, dispatch);
      setDropdownList({ [scopeType]: dropdownRes });
    }
  };

  const { errorState, onSubmitErrorValidator, onDismissErrorHandler } = useErrorHandler(formState, [
    {
      name: 'reassignSourceType',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'leadActivityStatus',
      fieldType: ErrorFieldType.MANDATORY,
    },
    {
      name: 'updateActivityStatus',
      fieldType: ErrorFieldType.MANDATORY,
    },
    ...(() => {
      const errorFieldDef: ErrorFieldDef[] = [];
      if (formState.reassignSourceType === ReassignSourceTypeEnum.SPECIFIC_LEAD_CAMPAIGN) {
        errorFieldDef.push(
          {
            name: 'specificCampaignList',
            fieldType: ErrorFieldType.MANDATORY,
          },
          {
            name: 'emptySpecificCampaignList',
            fieldType: ErrorFieldType.IMMEDIATE,
            condition: () => {
              if (formState.specificCampaignList) {
                return formState.specificCampaignList.length === 0;
              } else {
                return false;
              }
            },
          },
        );
      } else if (formState.reassignSourceType === ReassignSourceTypeEnum.SPECIFIC_LEAD_SOURCE) {
        errorFieldDef.push(
          {
            name: 'specificLeadSourceList',
            fieldType: ErrorFieldType.MANDATORY,
          },
          {
            name: 'emptySpecificLeadSourceList',
            fieldType: ErrorFieldType.IMMEDIATE,
            condition: () => {
              if (formState.specificLeadSourceList) {
                return formState.specificLeadSourceList.length === 0;
              } else {
                return false;
              }
            },
          },
        );
      }
      return errorFieldDef;
    })(),
  ]);

  const onValidateAgentList = () => {
    const newAgentListErrorState = formState.reassignAgentList.map((item) => ({
      fromAgent: item.fromAgent === '',
      toAgent: item.toAgent === '',
    }));
    setAgentListErrorState(newAgentListErrorState);
    return {
      hasError: newAgentListErrorState.findIndex((state) => state.fromAgent || state.toAgent) !== -1,
    };
  };

  const onSubmit = async () => {
    const { hasError } = onSubmitErrorValidator();
    const agentListValidator = onValidateAgentList();
    if (!hasError && !agentListValidator.hasError) {
      const body: ReassignLeadBody = {
        reassignAgentList: formState.reassignAgentList,
        reassignSourceType: formState.reassignSourceType || '',
        specificCampaignList:
          formState.reassignSourceType === ReassignSourceTypeEnum.SPECIFIC_LEAD_CAMPAIGN
            ? formState.specificCampaignList
            : undefined,
        specificLeadSourceList:
          formState.reassignSourceType === ReassignSourceTypeEnum.SPECIFIC_LEAD_SOURCE
            ? formState.specificLeadSourceList
            : undefined,
        leadActivityStatus: formState.leadActivityStatus || '',
        isCopyEngagement: formState.isCopyEngagement,
        isCopyCalendar: formState.isCopyCalendar,
        updateActivityStatus: formState.updateActivityStatus || '',
        isAddReassignLabel: formState.isAddReassignLabel,
        isSendNotification: formState.isSendNotification,
      };
      try {
        await reassignLead(body, dispatch);
        dispatch(
          appendAlertItem([
            {
              severity: AlertType.SUCCESS,
              title: 'Success',
              content: 'Lead reassigned successfully',
            },
          ]),
        );
        history.push(reassignLeadPath);
      } catch (err) {}
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        <div className={classes.rowContainer}>
          <div className={commonClasses.header}>{Translation('title.pulseleads.agent.reassignLead')}</div>
        </div>
      </div>

      <div className={classes.sectionMargin}>
        {formState.reassignAgentList.map((reassignAgentItem, index) => (
          <>
            <div className={classes.rowContainer}>
              <div className={classes.fieldContainer}>
                <span>
                  {Translation('pulseleads.agent.reassignLead.from')}
                  <span className={classes.mandatory}>*</span> :
                </span>
              </div>
              <div style={{ flexGrow: 1 }} className={classes.rowContainer}>
                <TextField
                  style={{ width: 250, marginRight: 10 }}
                  error={agentListErrorState[index].fromAgent}
                  margin="dense"
                  variant="outlined"
                  placeholder={'Agent ID'}
                  helperText={agentListErrorState[index].fromAgent && MANDATORY_FIELD_ERROR_TEXT}
                  value={reassignAgentItem.fromAgent}
                  onChange={(e) => {
                    const agentCode = e.target.value.replace(/[^0-9]/g, '');
                    const newReassignAgentList = [...formState.reassignAgentList];
                    newReassignAgentList[index].fromAgent = agentCode;
                    formDispatch({
                      type: 'MODIFY_FIELD',
                      payload: { field: 'reassignAgentList', value: newReassignAgentList },
                    });

                    const newAgentListErrorState = [...agentListErrorState];
                    newAgentListErrorState[index].fromAgent = agentCode === '';
                    setAgentListErrorState(newAgentListErrorState);
                  }}
                />
                {index !== 0 && (
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => {
                      const newReassignAgentList = [...formState.reassignAgentList];
                      newReassignAgentList.splice(index, 1);
                      formDispatch({
                        type: 'MODIFY_FIELD',
                        payload: { field: 'reassignAgentList', value: newReassignAgentList },
                      });

                      const newAgentListErrorState = [...agentListErrorState];
                      newAgentListErrorState.splice(index, 1);
                      setAgentListErrorState(newAgentListErrorState);
                    }}
                  >
                    <RemoveCircle color="secondary" className={classes.icon} />
                  </IconButton>
                )}
              </div>
            </div>

            <div className={classes.rowContainer}>
              <div className={classes.fieldContainer}>
                <span>
                  {Translation('pulseleads.agent.reassignLead.to')}
                  <span className={classes.mandatory}>*</span> :
                </span>
              </div>
              <div style={{ flexGrow: 1 }} className={classes.rowContainer}>
                <TextField
                  style={{ width: 250, marginRight: 10 }}
                  error={agentListErrorState[index].toAgent}
                  margin="dense"
                  variant="outlined"
                  placeholder={'Agent ID'}
                  helperText={agentListErrorState[index].toAgent && MANDATORY_FIELD_ERROR_TEXT}
                  value={reassignAgentItem.toAgent}
                  onChange={(e) => {
                    const agentCode = e.target.value.replace(/[^0-9]/g, '');
                    const newReassignAgentList = [...formState.reassignAgentList];
                    newReassignAgentList[index].toAgent = agentCode;
                    formDispatch({
                      type: 'MODIFY_FIELD',
                      payload: { field: 'reassignAgentList', value: newReassignAgentList },
                    });

                    const newAgentListErrorState = [...agentListErrorState];
                    newAgentListErrorState[index].toAgent = agentCode === '';
                    setAgentListErrorState(newAgentListErrorState);
                  }}
                />
                {index === formState.reassignAgentList.length - 1 && (
                  <IconButton
                    className={classes.iconButton}
                    onClick={() => {
                      formDispatch({
                        type: 'MODIFY_FIELD',
                        payload: {
                          field: 'reassignAgentList',
                          value: [...formState.reassignAgentList, { ...initialReassignAgentItem }],
                        },
                      });
                      setAgentListErrorState([...agentListErrorState, { ...initialAgentListErrorState }]);
                    }}
                  >
                    <AddCircle color="secondary" className={classes.icon} />
                  </IconButton>
                )}
              </div>
            </div>
          </>
        ))}

        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span>
              {Translation('pulseleads.agent.reassignLead.source')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <FormControl error={errorState.mandatory.reassignSourceType} margin="dense" variant="outlined">
            <Select
              style={{ minWidth: 350 }}
              value={formState.reassignSourceType || ''}
              onChange={(e) => {
                onDismissErrorHandler('reassignSourceType', e.target.value);
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'reassignSourceType', value: e.target.value } });
                reloadDropdown(e.target.value as ReassignSourceTypeEnum);
              }}
            >
              {Object.values(ReassignSourceTypeEnum).map((value) => (
                <MenuItem key={value} value={value}>
                  {Translation(`pulseleads.agent.reassignLead.source.${value}`)}
                </MenuItem>
              ))}
            </Select>
            {errorState.mandatory.reassignSourceType && <FormHelperText>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>}
          </FormControl>
        </div>

        {formState.reassignSourceType === ReassignSourceTypeEnum.SPECIFIC_LEAD_CAMPAIGN && (
          <div className={classes.rowContainer} style={{ marginTop: 5 }}>
            <div className={classes.fieldContainer} />
            <div>
              <div>
                <span style={{ marginRight: 5 }}>
                  {Translation('pulseleads.agent.reassignLead.campaign')}
                  <span className={classes.mandatory}>*</span>
                </span>
                <IconButton className={classes.iconButton} onClick={() => reloadDropdown(formState.reassignSourceType)}>
                  <Refresh />
                </IconButton>
              </div>
              <FormControl
                error={errorState.immediate.emptySpecificCampaignList || errorState.mandatory.specificCampaignList}
                margin="dense"
                variant="outlined"
              >
                <Select
                  style={{ minWidth: 350, maxWidth: 350 }}
                  multiple
                  value={formState.specificCampaignList || []}
                  onChange={(e) => {
                    onDismissErrorHandler('specificCampaignList', e.target.value);
                    formDispatch({
                      type: 'MODIFY_FIELD',
                      payload: { field: 'specificCampaignList', value: e.target.value || [] },
                    });
                  }}
                >
                  {dropdownList &&
                    dropdownList[LeadScopingTypeEnum.CAMPAIGN] &&
                    dropdownList[LeadScopingTypeEnum.CAMPAIGN].map((dropdownItem) => (
                      <MenuItem key={dropdownItem.key} value={dropdownItem.key}>
                        {dropdownItem.value}
                      </MenuItem>
                    ))}
                </Select>
                {(errorState.immediate.emptySpecificCampaignList || errorState.mandatory.specificCampaignList) && (
                  <FormHelperText>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>
        )}

        {formState.reassignSourceType === ReassignSourceTypeEnum.SPECIFIC_LEAD_SOURCE && (
          <div className={classes.rowContainer} style={{ marginTop: 5 }}>
            <div className={classes.fieldContainer} />
            <div>
              <div>
                <span style={{ marginRight: 5 }}>
                  {Translation('pulseleads.agent.reassignLead.leadSource')}
                  <span className={classes.mandatory}>*</span>
                </span>
                <IconButton className={classes.iconButton} onClick={() => reloadDropdown(formState.reassignSourceType)}>
                  <Refresh />
                </IconButton>
              </div>
              <FormControl
                error={errorState.immediate.emptySpecificLeadSourceList || errorState.mandatory.specificLeadSourceList}
                margin="dense"
                variant="outlined"
              >
                <Select
                  style={{ minWidth: 350, maxWidth: 350 }}
                  multiple
                  value={formState.specificLeadSourceList || []}
                  onChange={(e) => {
                    onDismissErrorHandler('specificLeadSourceList', e.target.value);
                    formDispatch({
                      type: 'MODIFY_FIELD',
                      payload: { field: 'specificLeadSourceList', value: e.target.value || [] },
                    });
                  }}
                >
                  {dropdownList &&
                    dropdownList[LeadScopingTypeEnum.LEAD_SOURCE] &&
                    dropdownList[LeadScopingTypeEnum.LEAD_SOURCE].map((dropdownItem) => (
                      <MenuItem key={dropdownItem.key} value={dropdownItem.key}>
                        {dropdownItem.value}
                      </MenuItem>
                    ))}
                </Select>
                {(errorState.immediate.emptySpecificLeadSourceList || errorState.mandatory.specificLeadSourceList) && (
                  <FormHelperText>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>
                )}
              </FormControl>
            </div>
          </div>
        )}

        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span>
              {Translation('pulseleads.agent.reassignLead.leadActivityStatus')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <FormControl error={errorState.mandatory.leadActivityStatus} margin="dense" variant="outlined">
            <Select
              style={{ minWidth: 350 }}
              value={formState.leadActivityStatus || ''}
              onChange={(e) => {
                onDismissErrorHandler('leadActivityStatus', e.target.value);
                formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'leadActivityStatus', value: e.target.value } });
              }}
            >
              {Object.values(LeadActivityStatusEnum).map((value) => (
                <MenuItem key={value} value={value}>
                  {Translation(`pulseleads.agent.reassignLead.leadActivityStatus.${value}`)}
                </MenuItem>
              ))}
            </Select>
            {errorState.mandatory.leadActivityStatus && <FormHelperText>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>}
          </FormControl>
        </div>

        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span>{Translation('pulseleads.agent.reassignLead.isCopyEngagement')} :</span>
          </div>
          <Checkbox
            className={classes.checkboxPadding}
            checked={formState.isCopyEngagement}
            onChange={(e) => {
              formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'isCopyEngagement', value: e.target.checked } });
            }}
          />
        </div>

        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span>{Translation('pulseleads.agent.reassignLead.isCopyCalendar')} :</span>
          </div>
          <Checkbox
            className={classes.checkboxPadding}
            checked={formState.isCopyCalendar}
            onChange={(e) => {
              formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'isCopyCalendar', value: e.target.checked } });
            }}
          />
        </div>

        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span>
              {Translation('pulseleads.agent.reassignLead.updateActivityStatus')}
              <span className={classes.mandatory}>*</span> :
            </span>
          </div>
          <FormControl error={errorState.mandatory.updateActivityStatus} margin="dense" variant="outlined">
            <Select
              style={{ minWidth: 350 }}
              value={formState.updateActivityStatus || ''}
              onChange={(e) => {
                onDismissErrorHandler('updateActivityStatus', e.target.value);
                formDispatch({
                  type: 'MODIFY_FIELD',
                  payload: { field: 'updateActivityStatus', value: e.target.value },
                });
              }}
            >
              {Object.values(UpdateActivityStatusEnum).map((value) => (
                <MenuItem key={value} value={value}>
                  {Translation(`pulseleads.agent.reassignLead.updateActivityStatus.${value}`)}
                </MenuItem>
              ))}
            </Select>
            {errorState.mandatory.updateActivityStatus && <FormHelperText>{MANDATORY_FIELD_ERROR_TEXT}</FormHelperText>}
          </FormControl>
        </div>

        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span>{Translation('pulseleads.agent.reassignLead.isAddReassignLabel')} :</span>
          </div>
          <Checkbox
            className={classes.checkboxPadding}
            checked={formState.isAddReassignLabel}
            onChange={(e) => {
              formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'isAddReassignLabel', value: e.target.checked } });
            }}
          />
        </div>

        <div className={classes.rowContainer}>
          <div className={classes.fieldContainer}>
            <span>{Translation('pulseleads.agent.reassignLead.isSendNotification')} :</span>
          </div>
          <Checkbox
            className={classes.checkboxPadding}
            checked={formState.isSendNotification}
            onChange={(e) => {
              formDispatch({ type: 'MODIFY_FIELD', payload: { field: 'isSendNotification', value: e.target.checked } });
            }}
          />
          <span>{Translation('pulseleads.agent.reassignLead.isSendNotification.app')}</span>
        </div>
      </div>

      <div className={classes.footerContainer}>
        <Button variant="contained" color="secondary" onClick={onSubmit}>
          {Translation('title.pulseleads.agent.reassignLead')}
        </Button>
      </div>
    </div>
  );
};

export default ReassignLeadDetailPage;
